import { addDays, addMonths, addWeeks, addYears, endOfDay, endOfMonth, endOfWeek, endOfYear, getDaysInMonth, getWeeksInMonth, startOfMonth, startOfWeek, startOfYear } from 'date-fns'

// @dynamic
export class TimeUtils {
    /**
     * Generate a collection of labels/values for a specified range of years
     * @param min The minimum year to generate
     * @param max The maximum year to generate
     * @param addToDateLabel Wether or not to append '(year to date)' to the current year
     */
    public static generateYears(
        min: number,
        max: number,
        addToDateLabel: boolean = false
    ): {
        label: string
        start: Date
        end: Date
    }[] {
        let results: {
            label: string
            value: number
        }[] = []
        while (min <= max) {
            results.push({
                label: min.toString() + (min == new Date().getFullYear() && addToDateLabel ? ' (year to date)' : ''),
                value: min
            })
            min++
        }

        return results.map(y => {           
            return {
                label: y.label,
                start: this.recreateDate(startOfYear(new Date(y.value, 0, 1))),
                end: this.recreateDate(endOfYear(new Date(y.value, 11, 1)))
            }
        })
    }

    /**
     * Generate a collection of labels/values for the months of a specified year
     * @param year The year to generate months for
     * @param lowerYearBound The year to limit the bound of months for
     * @param lowerYearMonthBound The month number (natural index) to limit the bound for
     * @param addToDateLabel Wether or not to append '(month to date)' to the current month
     * @param omitMonthIfFirstOfCurrentMonth Omit the month if it is the first day of the month
     */
    public static generateMonths(
        year: number,
        lowerYearBound: number | null = null,
        lowerYearMonthBound: number = 0,
        addToDateLabel: boolean = false,
        omitMonthIfFirstOfCurrentMonth: boolean = false,
        omitCurrentMonth = false
    ): {
        label: string
        start: Date
        end: Date
    }[] {
        let months = [
            { label: 'January', value: 0 },
            { label: 'February', value: 1 },
            { label: 'March', value: 2 },
            { label: 'April', value: 3 },
            { label: 'May', value: 4 },
            { label: 'June', value: 5 },
            { label: 'July', value: 6 },
            { label: 'August', value: 7 },
            { label: 'September', value: 8 },
            { label: 'October', value: 9 },
            { label: 'November', value: 10 },
            { label: 'December', value: 11 }
        ]

        // we only have data from the lower bound onward
        if (year == lowerYearBound) {
            months = months.filter(m => m.value >= lowerYearMonthBound - 1)
        }

        let now = new Date()

        // if the selected year is equal to the current year - we need to include only data up to the current month
        if (year == now.getFullYear()) {
            months = months.filter(m => m.value <= new Date().getMonth())

            for (let i = 0; i < months.length; i++) {
                if (months[i].value == new Date().getMonth()) {
                    months[i].label = addToDateLabel ? months[i].label + ' (month to date)' : months[i].label
                    break
                }
            }
        }

        // if it's the first of the current month - omit it if specified
        if (year == now.getFullYear() && now.getDate() == 1 && omitMonthIfFirstOfCurrentMonth) {
            months = months.filter(m => m.value < now.getMonth())
        }
        if(year == now.getFullYear() && omitCurrentMonth){
            months = months.filter(m => m.value < now.getMonth())
        }

        return months.map(m => {
            return {
                label: m.label,
                start: this.recreateDate(startOfMonth(new Date(year, m.value, 1))),
                end: this.recreateDate(endOfMonth(new Date(year, m.value, 1)))
            }
        })
    }

    /**
     * Recreates a date as a UTC date
     * @param date A date
     */
    private static recreateDate(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate())
    }
    public static recreateDateAsUTC(date: Date): Date {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    }
}

